import { generateSrcSetAndSizes } from '@lib/services/image-service'
import type {
  BreakpointsAtDimension,
  GenerateSrcSetAndSizesParams,
} from '@lib/services/image-service'
import { getImageProps, type ImageProps as NextImageProps } from 'next/image'
import { ensureProtocol } from '@lib/utilities/page-utilities'
import { DEVICE_SIZES } from '@lib/constants-node.cjs'

/**
 * Maps column count to a CSS sizes string for <img>.
 * Image is sized by the viewportWidth/columns.
 * More columns = smaller images. For custom layouts, use granular breakpointWidths.
 * Note: If images are too large, increase the number to generate smaller images.
 */
const columnBasedImageSizes: Record<number, string> = {
  1: '100vw',
  2: '(max-width: 639px) 100vw, 50vw',
  3: '(max-width: 639px) 100vw, (max-width: 1023px) 50vw, 33vw',
  4: '(max-width: 639px) 100vw, (max-width: 1023px) 50vw, (max-width: 1279px) 33vw, 25vw',
  5: '(max-width: 639px) 100vw, (max-width: 1023px) 50vw, (max-width: 1279px) 33vw, (max-width: 1535px) 25vw, 20vw',
  6: '(max-width: 639px) 100vw, (max-width: 1023px) 50vw, (max-width: 1279px) 33vw, (max-width: 1535px) 25vw, (max-width: 1919px) 20vw, 16.66vw',
} as const

/**
 * @internal
 * Used by heightToWidths()
 * Enforces minimum width and minimum height for a single breakpoint.
 * - If height is undefined, returns minWidth.
 * - If computed width is less than minWidth, uses minWidth and adjusts height accordingly.
 * - If computed height is less than minHeight, uses minHeight to compute width.
 *
 * @param height - Desired image height for the breakpoint.
 * @param aspectRatio - Aspect ratio (width/height) for the image.
 * @param minWidth - Minimum allowed width for the breakpoint.
 * @param minHeight - Minimum allowed height for the breakpoint.
 * @returns The computed width for the breakpoint.
 */
function enforceMinDimensions(
  height: number | undefined,
  aspectRatio: number,
  minWidth: number,
  minHeight: number
): number {
  if (typeof height === 'number') {
    let computedWidth = Math.round(height * aspectRatio)
    let computedHeight = height
    // Enforce minWidth
    if (computedWidth < minWidth) {
      computedWidth = minWidth
      computedHeight = Math.round(minWidth / aspectRatio)
    }
    // Enforce minHeight
    if (computedHeight < minHeight) {
      computedWidth = Math.round(minHeight * aspectRatio)
    }
    return computedWidth
  }
  // If no height, fallback to minWidth
  return minWidth
}

/**
 * @internal
 * Used by <ImageBase>
 * Converts height breakpoints (breakpointHeights) to device width breakpoints (breakpointWidths) using the provided aspect ratio.
 * For each device breakpoint (xs, sm, md, lg, xl, max), if the corresponding height breakpoint is undefined, uses the next largest defined height breakpoint (cascading down).
 * Ensures the computed width for each device breakpoint is at least as wide as the corresponding DEVICE_SIZES value.
 * Prevents pixelation or stretching for hero/banner images with unusual aspect ratios.
 *
 * DEVICE_SIZES: [640, 768, 1024, 1280, 1536] (xs, sm, md, lg, xl)
 * Device breakpoint keys: xs, sm, md, lg, xl, max (max uses 1920px)
 *
 * Note: No matter how many height breakpoints are provided, all device width breakpoints (xs, sm, md, lg, xl, max) will be returned in the output.
 *
 * @param breakpointHeights - Responsive height breakpoints for each device breakpoint; primarily for hero/banner images.
 * @param aspectRatio - The aspect ratio (width/height) to use for conversion.
 * @returns Responsive width breakpoints for each device breakpoint.
 */
const heightsToWidths = (
  breakpointHeights: BreakpointsAtDimension,
  aspectRatio: number
): BreakpointsAtDimension => {
  const minWidths = {
    xs: DEVICE_SIZES[0],
    sm: DEVICE_SIZES[1],
    md: DEVICE_SIZES[2],
    lg: DEVICE_SIZES[3],
    xl: DEVICE_SIZES[4],
    max: 1920,
  }
  const breakpointOrder: (keyof BreakpointsAtDimension)[] = ['xs', 'sm', 'md', 'lg', 'xl', 'max']
  // For each breakpoint, if undefined, use the next largest defined value
  const heights: Partial<BreakpointsAtDimension> = {}
  for (let i = breakpointOrder.length - 1; i >= 0; i--) {
    const key = breakpointOrder[i]
    if (typeof breakpointHeights[key] === 'number') {
      heights[key] = breakpointHeights[key]
    } else {
      // Find next largest defined value
      let found: number | undefined = undefined
      for (let j = i + 1; j < breakpointOrder.length; j++) {
        const nextKey = breakpointOrder[j]
        if (typeof breakpointHeights[nextKey] === 'number') {
          found = breakpointHeights[nextKey]
          break
        }
      }
      heights[key] = found
    }
  }
  const result: Partial<BreakpointsAtDimension> = {}
  for (const key of breakpointOrder) {
    const height = heights[key]
    const minWidth = minWidths[key] ?? 0
    result[key] = enforceMinDimensions(
      height,
      aspectRatio,
      minWidth,
      typeof height === 'number' ? height! : 0
    )
  }
  return result as BreakpointsAtDimension
}

/**
 * @internal
 * Generates responsive image parameters (sizes, srcSet, computed width/height) for breakpoints.
 * Used to create optimal <img> attributes for responsive images.
 *
 * @param src - The image source URL.
 * @param breakpointWidths - Responsive widths for each breakpoint.
 * @param aspectRatio - The aspect ratio (width/height) for the image.
 * @param width - Optional explicit width override.
 * @param height - Optional explicit height override.
 * @returns An object containing customSizes, customSrcSet, computedWidth, and computedHeight.
 */
const getResponsiveImageParams = (
  src: string,
  breakpointWidths: BreakpointsAtDimension,
  aspectRatio: number,
  width?: number,
  height?: number
) => {
  const { sizes, srcSet } = generateSrcSetAndSizes({
    src: ensureProtocol(src),
    breakpointWidths,
    aspectRatio,
  } satisfies GenerateSrcSetAndSizesParams)
  const maxWidth = Math.max(...Object.values(breakpointWidths))
  return {
    customSizes: sizes,
    customSrcSet: srcSet.join(', '),
    computedWidth: width ?? maxWidth,
    computedHeight: height ?? Math.round(maxWidth / aspectRatio),
  }
}

/**
 * @internal
 * Validates that only one sizing strategy prop is provided.
 * Throws an error if more than one of breakpointWidths, breakpointHeights, or columnsMax is present.
 */
function validateSizingStrategy({
  breakpointWidths,
  breakpointHeights,
  columnsMax,
}: {
  breakpointWidths?: BreakpointsAtDimension
  breakpointHeights?: BreakpointsAtDimension
  columnsMax?: number
}) {
  const hasWidths = breakpointWidths !== undefined
  const hasHeights = breakpointHeights !== undefined
  // Only consider columnsMax as 'provided' if neither breakpoints are present
  const hasColumns = !hasWidths && !hasHeights && columnsMax !== undefined
  const providedCount = [hasWidths, hasHeights, hasColumns].filter(Boolean).length
  if (providedCount > 1) {
    throw new Error(
      'ImageBase: Only one of breakpointWidths, breakpointHeights, or columnsMax may be provided.'
    )
  }
}

/**
 * @internal
 * Calculates image loading attributes (priority, loading, fetchPriority) from isAboveTheFold.
 *
 * Summary of output:
 * - If isAboveTheFold is true: returns { priority: true, loading: 'eager', fetchPriority: 'high' }
 * - If isAboveTheFold is false/undefined: returns { priority: false, loading: 'lazy', fetchPriority: 'auto' }
 * This enforces Next.js constraints and best-practice defaults for LCP/above-the-fold images.
 *
 * @param isAboveTheFold - Indicates if the image is above the fold (LCP candidate).
 * @returns Object with priority, loading, and fetchPriority attributes for Next.js <img>.
 */
function getImageLoadingAttributes(isAboveTheFold: boolean | undefined): {
  priority: boolean
  loading: 'eager' | 'lazy' | undefined
  fetchPriority: 'high' | 'auto' | 'low' | undefined
} {
  const priority = !!isAboveTheFold
  // Enforce Next.js constraint: priority=true must have loading='eager'
  const loading: 'eager' | 'lazy' = priority ? 'eager' : 'lazy'
  const fetchPriority: 'high' | 'auto' | 'low' = priority ? 'high' : 'auto'
  return { priority, loading, fetchPriority }
}

/**
 * @internal
 * Centralizes calculation of responsive image attributes for <img> elements.
 * Given a source URL, breakpoints, aspect ratio, and optional explicit width/height,
 * this function returns the optimal sizes, srcSet, and computed dimensions for the image.
 *
 * - If no breakpointWidths are provided, returns undefined for sizes/srcSet and uses explicit width/height.
 * - If breakpointWidths are provided, uses getResponsiveImageParams to generate all responsive attributes.
 * - Used to ensure images are sharp, not pixelated, and visually correct across all device breakpoints.
 *
 * @param src - The image source URL.
 * @param breakpointWidths - Responsive widths for each device breakpoint (or undefined).
 * @param aspectRatio - Aspect ratio (width/height) for the image.
 * @param width - Optional explicit width override.
 * @param height - Optional explicit height override.
 * @returns An object containing customSizes, customSrcSet, computedWidth, and computedHeight for the image.
 */
function getEffectiveImageAttributes(
  src: string,
  breakpointWidths: BreakpointsAtDimension | undefined,
  aspectRatio: number,
  width?: number,
  height?: number
) {
  if (!breakpointWidths) {
    return {
      customSizes: undefined,
      customSrcSet: undefined,
      computedWidth: width,
      computedHeight: height,
    }
  }
  const params = getResponsiveImageParams(src, breakpointWidths, aspectRatio, width, height)
  return {
    customSizes: params.customSizes,
    customSrcSet: params.customSrcSet,
    computedWidth: params.computedWidth,
    computedHeight: params.computedHeight,
  }
}

/**
 * Props for the ImageBase component.
 * Provides a robust, maintainable interface for rendering responsive images with Next.js and external CDNs (e.g., Contentful).
 *
 * Sizing strategies (mutually exclusive):
 *   - Only one of `breakpointWidths`, `breakpointHeights`, or `columnsMax` should be provided per instance.
 *   - If both `breakpointWidths` and `breakpointHeights` are provided, an error will be thrown.
 *   - If neither is provided, `columnsMax` (column-based sizing) will be used. If `columnsMax` is also not provided, it defaults to 3 columns.
 *   - `columnsMax` is ignored if any breakpoints are provided.
 *   - `breakpointHeights` is primarily intended for hero/banner images where height-driven sizing is required.
 *
 * Responsive widths/heights for each breakpoint (see BreakpointsAtDimension for pixel ranges).
 *   - Undefined values will cascade: if a breakpoint (e.g., md) is undefined, the next largest defined value (e.g., lg) will be used for that breakpoint.
 *
 * Loading strategy:
 *   - Use `isAboveTheFold` to indicate above-the-fold/LCP images. This will automatically set optimal loading attributes (`priority`, `loading`, `fetchPriority`).
 *   - Do not pass `priority`, `loading`, or `fetchPriority` directly; these are calculated internally.
 *
 *
 * Props are documented individually below for best intellisense and clarity.
 */
export type ImageBaseProps = {
  /**
   * Responsive widths for each breakpoint (general images).
   * See BreakpointsAtDimension for pixel ranges.
   */
  breakpointWidths?: BreakpointsAtDimension
  /**
   * Responsive heights for each breakpoint; mainly for hero/banner images.
   * Only one of breakpointWidths or breakpointHeights should be provided per instance.
   */
  breakpointHeights?: BreakpointsAtDimension
  /**
   * Aspect ratio (width / height) for the image.
   */
  aspectRatio?: number
  /**
   * Number of columns for default column-based sizing (used only if no breakpoints are provided).
   */
  columnsMax?: number
  /**
   * If true, image is considered above-the-fold (LCP candidate).
   * This is the main prop to use for above-the-fold/LCP images. It will default loading to 'eager', fetchPriority to 'high', and priority to true.
   * For below-the-fold images, leave unset or false.
   * Advanced: override loading/fetchPriority only for edge cases.
   */
  isAboveTheFold?: boolean
} & Omit<NextImageProps, 'loading' | 'fetchPriority' | 'priority'>

/**
 * Main ImageBase component for rendering responsive images with Next.js and external CDNs (e.g., Contentful).
 *
 * Overview:
 * - Supports three mutually exclusive sizing strategies: `breakpointWidths`, `breakpointHeights`, or `columnsMax`.
 * - Only one sizing strategy may be provided per instance. If more than one is provided, an error is thrown.
 * - If neither `breakpointWidths` nor `breakpointHeights` is provided, `columnsMax` (column-based sizing) will be used (default: 3 columns).
 * - `breakpointHeights` is primarily intended for hero/banner images where height-driven sizing is required.
 * - Automatically calculates optimal `sizes`, `srcSet`, and loading attributes for best performance and LCP.
 * - Handles accessibility by always setting `alt` explicitly.
 *
 * Sizing strategy notes:
 * - If you use `breakpointWidths` or `breakpointHeights`, the image will have explicit width/height and does NOT require a parent aspect-ratio wrapper.
 * - If you use `columnsMax` (default fill mode), you MUST wrap the image in a parent with `relative` positioning and an aspect-ratio class (e.g., `aspect-video`). This ensures the image is sized correctly by Next.js fill mode.
 *
 * Example usage for fill mode (columnsMax):
 * ```tsx
 * <div className="relative aspect-video">
 *   <ImageBase src={...} columnsMax={4} ...otherProps />
 * </div>
 * ```
 * Props:
 * @property {BreakpointsAtDimension} breakpointWidths - Responsive widths for each breakpoint (general images).
 * @property {BreakpointsAtDimension} breakpointHeights - Responsive heights for each breakpoint; mainly for hero/banner images.
 * @property {number} aspectRatio - Aspect ratio (width / height) for the image.
 * @property {number} columnsMax - Number of columns for default column-based sizing (used only if no breakpoints are provided).
 * @property {boolean} isAboveTheFold - If true, image is considered above-the-fold (LCP candidate); sets optimal loading attributes automatically.
 */
const ImageBase = (props: ImageBaseProps) => {
  const {
    breakpointWidths,
    breakpointHeights,
    columnsMax = 3,
    aspectRatio = 16 / 9,
    width,
    height,
    isAboveTheFold = false,
    ...rest
  } = props

  // throws error if more than one sizing strategy is provided
  validateSizingStrategy({ breakpointWidths, breakpointHeights, columnsMax })

  // Coerce width and height to numbers if they are string values
  const numericWidth = typeof width === 'string' ? Number(width) : width
  const numericHeight = typeof height === 'string' ? Number(height) : height

  // If height breakpoints are provided, convert to width breakpoints using aspect ratio.
  // See heightsToWidths JSDoc for full details. Only one sizing strategy should be used per instance.
  let effectiveBreakpointWidths = breakpointWidths
  if (breakpointHeights) {
    effectiveBreakpointWidths = heightsToWidths(breakpointHeights, aspectRatio)
  }

  // Calculate all responsive image attributes (sizes, srcSet, width, height) for optimal rendering
  const { customSizes, customSrcSet, computedWidth, computedHeight } = getEffectiveImageAttributes(
    String(rest.src),
    effectiveBreakpointWidths,
    aspectRatio,
    numericWidth,
    numericHeight
  )

  const computedSizes = customSizes ?? rest.sizes ?? columnBasedImageSizes[columnsMax] ?? '100vw'

  // Calculate image loading attributes (priority, loading, fetchPriority) based solely on isAboveTheFold.
  // This enforces best-practice defaults and prevents external override.
  const {
    priority: effectivePriority,
    loading: effectiveLoading,
    fetchPriority: effectiveFetchPriority,
  } = getImageLoadingAttributes(isAboveTheFold)

  // Remove 'src' and 'alt' from ...rest to avoid duplicate keys
  const { src, alt, ...restWithoutSrcAlt } = rest
  const { props: imageProps } = getImageProps({
    fill: !breakpointWidths && !breakpointHeights,
    loading: effectiveLoading,
    fetchPriority: effectiveFetchPriority,
    sizes: computedSizes,
    width: computedWidth,
    height: computedHeight,
    priority: effectivePriority,
    src: ensureProtocol(String(src)),
    alt: alt ?? '',
    ...restWithoutSrcAlt,
  })

  // Override srcSet if custom CDN candidates are present.
  if (customSrcSet) {
    imageProps.srcSet = customSrcSet
  }

  return (
    // Explicitly set alt to satisfy accessibility requirements and static analysis tools (e.g., SonarQube),
    // which does not detect alt when only spread via imageProps.
    // eslint-disable-next-line @next/next/no-img-element
    <img {...imageProps} alt={imageProps.alt} />
  )
}

export default ImageBase
